import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { DeleteOutlineRounded, EditRounded } from '@mui/icons-material';
import { IconButton, TextField } from '@mui/material';

import {
  Row,
  RowActions,
  RowLabel,
  RowSelectButton,
} from '@/components/shared/AgentAssistantPopup/styles';
import type { ChatSession } from '@/hooks/useAgentChatSessions';
import { useSnackbar } from '@/providers/SnackbarProvider/SnackbarProvider';

const UNTITLED = 'New conversation';

type RowMode = 'view' | 'editing';

export type ConversationHistoryRowProps = {
  session: ChatSession;
  active: boolean;
  onSelect: (sessionId: string) => void;
  onRequestDelete: (session: ChatSession) => void;
  onRename?: (sessionId: string, chatName: string) => Promise<void>;
};

export const ConversationHistoryRow = ({
  session,
  active,
  onSelect,
  onRequestDelete,
  onRename,
}: ConversationHistoryRowProps) => {
  const snackbar = useSnackbar();
  const displayName = session.chatName ?? UNTITLED;
  const [mode, setMode] = useState<RowMode>('view');
  const [draftName, setDraftName] = useState(displayName);
  const committedRef = useRef(false);

  // Keep the draft in sync if the name changes upstream while idle.
  useEffect(() => {
    if (mode === 'view') setDraftName(displayName);
  }, [displayName, mode]);

  const startEditing = () => {
    committedRef.current = false;
    setDraftName(displayName);
    setMode('editing');
  };

  const commitEditing = async () => {
    // Enter commits via onKeyDown and switches to 'view', which triggers the
    // input's onBlur — guard against committing (and firing onRename) twice.
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = draftName.trim();
    setMode('view');
    if (trimmed && trimmed !== displayName) {
      try {
        await onRename?.(session.sessionId, trimmed);
      } catch {
        snackbar({
          message: "Couldn't rename conversation. Please try again.",
          severity: 'error',
        });
      }
    }
  };

  const cancelEditing = () => {
    setDraftName(displayName);
    setMode('view');
  };

  if (mode === 'editing') {
    return (
      <Row active={active}>
        <TextField
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commitEditing();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelEditing();
            }
          }}
          onBlur={commitEditing}
          size="small"
          fullWidth
          autoFocus
          inputProps={{ 'aria-label': 'Conversation name' }}
          sx={(theme) => ({
            '& .MuiInputBase-input': {
              fontSize: theme.typography.body2.fontSize,
              lineHeight: theme.typography.body2.lineHeight,
            },
          })}
        />
      </Row>
    );
  }

  return (
    <Row active={active}>
      <RowSelectButton
        type="button"
        active={active}
        onClick={() => onSelect(session.sessionId)}
      >
        <RowLabel variant="body2">{displayName}</RowLabel>
      </RowSelectButton>
      <RowActions className="row-actions">
        <IconButton
          size="small"
          aria-label="Rename conversation"
          onClick={startEditing}
        >
          <EditRounded fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label="Delete conversation"
          onClick={() => onRequestDelete(session)}
        >
          <DeleteOutlineRounded fontSize="small" />
        </IconButton>
      </RowActions>
    </Row>
  );
};
